import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import type { ReactNode } from 'react';

import { Menu } from '../menu';

import { useDropdown } from './context';

export interface DropdownMenuProps {
  /** Menu content (DropdownMenuItem, DropdownMenuSeparator) */
  children: ReactNode;
  /** Maximum height for the items area in pixels */
  maxHeight?: number;
  /** Additional className */
  className?: string;
}

const menuVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.15,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.1,
      ease: [0.4, 0, 1, 1] as const,
    },
  },
};

/**
 * DropdownMenu - renders the floating menu container
 *
 * Uses Menu internally with floating/portal functionality.
 *
 * @example
 * ```tsx
 * <DropdownMenu>
 *   <DropdownMenuItem id="edit">Edit</DropdownMenuItem>
 *   <DropdownMenuItem id="duplicate">Duplicate</DropdownMenuItem>
 *   <DropdownMenuSeparator />
 *   <DropdownMenuItem id="delete" destructive>Delete</DropdownMenuItem>
 * </DropdownMenu>
 * ```
 */
export function DropdownMenu({ children, maxHeight = 240, className = '' }: DropdownMenuProps) {
  const { isOpen, setFloating, floatingStyles, context, getFloatingProps, size } = useDropdown();

  return (
    <FloatingPortal>
      <AnimatePresence>
        {isOpen ? (
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={setFloating}
              style={floatingStyles}
              className="z-50 outline-none"
              {...getFloatingProps()}
            >
              <motion.div
                className={`bg-white border border-uiv2-border-secondary rounded-pb-2 shadow-uiv2-lg origin-top ${className}`}
                variants={menuVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <Menu size={size} maxHeight={maxHeight}>
                  {children}
                </Menu>
              </motion.div>
            </div>
          </FloatingFocusManager>
        ) : null}
      </AnimatePresence>
    </FloatingPortal>
  );
}
